// Phone readiness and call-control gating. Controls render from server truth
// (Call.allowed_actions, capabilities) plus the device's actual engine state. Nothing optimistic.
import type { Agent, Call, Capability } from '@bcio-voice/contracts';
import { gate } from '../lib/capabilities.ts';
import { type Diagnostic, featureNotice, phoneReasonText, SETUP_NOT_FINISHED } from '../lib/userCopy.ts';
import { type BridgeCall, type BridgeSnapshot, type MicPermission } from './bridge.ts';
import type { DeviceView } from './deviceView.ts';
import type { OwnershipSnapshot } from './phoneOwnership.ts';
import { lastFailureSentence, transportDetail } from './transportView.ts';
import type { ActionName } from './commands.ts';

export type ReadinessState =
  | 'offline'
  | 'capability_off'
  | 'no_permission'
  | 'no_device'
  | 'needs_microphone'
  | 'unconfigured'
  | 'unsupported'
  | 'in_another_tab'
  | 'registering'
  | 'reconnecting'
  | 'stopped'
  | 'revoked'
  | 'failed'
  | 'ready';

/** The one action that helps, if any. */
export type ReadinessAction = 'setup_device' | 'allow_microphone' | 'retry' | 'use_here' | 'turn_on' | null;

export interface Readiness {
  state: ReadinessState;
  label: string;
  /** Plain-language sentence for the main flow. */
  reason: string;
  action: ReadinessAction;
  /** Server/engine codes for "Technical details"; never shown in the main flow. */
  diagnostic: Diagnostic | null;
}

export interface ReadinessInput {
  online: boolean;
  callingInternal: Capability | null;
  canManageDevices: boolean;
  device: DeviceView | null;
  mic: MicPermission;
  bridge: BridgeSnapshot;
}

const r = (state: ReadinessState, label: string, reason: string, action: ReadinessAction, diagnostic: Diagnostic | null = null): Readiness => ({ state, label, reason, action, diagnostic });

export function phoneReadiness(input: ReadinessInput): Readiness {
  if (!input.online) return r('offline', 'Offline', phoneReasonText('network_offline'), null);
  const g = gate(input.callingInternal);
  if (g.render !== 'normal' && g.render !== 'degraded') {
    return r('capability_off', 'Not set up', featureNotice('calling', g) ?? SETUP_NOT_FINISHED, null, { code: input.callingInternal?.reason_code ?? null, detail: input.callingInternal?.detail ?? null });
  }
  if (!input.canManageDevices) return r('no_permission', 'Not permitted', 'Your role doesn’t include using a phone on this device.', null);
  if (!input.device || input.device.revoked_at) return r('no_device', 'Not set up', phoneReasonText('no_device'), 'setup_device');

  const { phone, bootstrap } = input.bridge;
  const diag = (code: string | null): Diagnostic => ({ code, detail: bootstrap?.reasonCode ? `bootstrap ${bootstrap.status}: ${bootstrap.reasonCode}` : bootstrap ? `bootstrap ${bootstrap.status}` : null });
  // Connection states also carry the SIP WebSocket diagnostic: why the last try failed, in words, and
  // the sanitized endpoint, close code and timings under "Technical details".
  const why = lastFailureSentence(phone.transport);
  const said = (sentence: string) => (why ? `${sentence} ${why}` : sentence);
  const tdiag = (code: string | null): Diagnostic => {
    const base = diag(code);
    const detail = [base.detail, transportDetail(phone.transport)].filter(Boolean).join('; ');
    return { code, detail: detail || null };
  };
  if (input.bridge.engine === 'none') {
    const reason = input.device.bootstrap?.status === 'unconfigured' ? input.device.bootstrap.reason_code : phone.reason;
    return r('unconfigured', 'Not ready', phoneReasonText(reason), null, diag(reason));
  }
  if (input.mic === 'denied' && phone.state !== 'registered') return r('needs_microphone', 'Microphone blocked', phoneReasonText('microphone_denied'), 'allow_microphone', diag('microphone_denied'));
  switch (phone.state) {
    case 'registered':
      return phone.acceptingCalls ? r('ready', 'Ready', 'Your phone is connected.', null) : r('registering', 'Connecting', 'Connecting your phone…', null, tdiag(phone.reason));
    case 'starting':
    case 'connecting':
      // Between retries the phone flips back to connecting; the earlier failure still explains the wait.
      return r('registering', 'Connecting', said('Connecting your phone…'), why ? 'retry' : null, tdiag(phone.reason));
    case 'needs_microphone':
      return phone.reason === 'microphone_denied'
        ? r('needs_microphone', 'Microphone blocked', phoneReasonText('microphone_denied'), 'allow_microphone', diag(phone.reason))
        : r('needs_microphone', 'Needs microphone', phoneReasonText('microphone_permission_required'), 'allow_microphone', diag(phone.reason));
    case 'in_another_tab':
      return r('in_another_tab', 'In another tab', phoneReasonText('phone_active_in_another_tab'), 'use_here', diag(phone.reason));
    case 'reconnecting':
      return r('reconnecting', 'Reconnecting', said(phone.attempt > 1 ? `Reconnecting your phone (attempt ${phone.attempt})…` : 'Reconnecting your phone…'), 'retry', tdiag(phone.reason));
    case 'offline':
      return phone.reason === 'network_offline'
        ? r('offline', 'Offline', phoneReasonText('network_offline'), 'retry', diag(phone.reason))
        : r('offline', 'Offline', said(phoneReasonText('retries_exhausted')), 'retry', tdiag(phone.reason));
    case 'stopped':
      return r('stopped', 'Off', phoneReasonText('user_unregistered'), 'turn_on', diag(phone.reason));
    case 'revoked':
      return r('revoked', 'Removed', phoneReasonText('device_revoked'), 'setup_device', diag(phone.reason));
    case 'blocked': {
      const reason = phone.reason ?? '';
      if (reason === 'insecure_context' || reason === 'webrtc_unsupported') return r('unsupported', 'Not supported', phoneReasonText(reason), null, diag(reason));
      if (reason === 'bootstrap_expired') return r('reconnecting', 'Reconnecting', phoneReasonText('bootstrap_expired'), 'retry', diag(reason));
      return r('unconfigured', 'Not ready', phoneReasonText(reason.startsWith('bootstrap_') ? 'bootstrap_unconfigured' : reason), 'retry', diag(reason));
    }
    default:
      return r('unconfigured', 'Not ready', phoneReasonText(phone.reason), null, diag(phone.reason));
  }
}

export interface ControlGate {
  visible: boolean;
  enabled: boolean;
  reason: string | null;
}

/**
 * Gate a single call control. Answer additionally needs this device's own verified offer for
 * exactly this call (the engine correlates the SIP leg to the call ID before it exposes it).
 * Decline and hang-up are never blocked by local device state.
 */
export function controlGate(call: Call, action: ActionName, ctx: { pending: boolean; readiness: Readiness; engineCall?: BridgeCall | null }): ControlGate {
  if ((action === 'hangup' || action === 'reject') && call.state !== 'ended' && call.state !== 'failed') return { visible: true, enabled: true, reason: null };
  const listed = call.allowed_actions.includes(action);
  if (!listed && call.allowed_actions.length === 0) return { visible: false, enabled: false, reason: null };
  if (!listed) {
    const toggles: Partial<Record<ActionName, ActionName>> = { hold: 'resume', resume: 'hold' };
    const partner = toggles[action];
    const visible = partner ? !call.allowed_actions.includes(partner) && (call.state === 'active' || call.state === 'held') : false;
    return { visible, enabled: false, reason: visible ? 'Not available in the current call state.' : null };
  }
  // Ending or declining a call is never held behind another pending command.
  if (ctx.pending && action !== 'hangup' && action !== 'reject') return { visible: true, enabled: false, reason: 'Waiting for the phone system to confirm the last action.' };
  if (action === 'answer') {
    if (ctx.readiness.state !== 'ready') return { visible: true, enabled: false, reason: `Connect your phone first. ${ctx.readiness.reason}` };
    if (!ctx.engineCall || ctx.engineCall.origin !== 'inbound' || ctx.engineCall.phase !== 'offered') {
      return { visible: true, enabled: false, reason: 'This call hasn’t reached this browser yet.' };
    }
  }
  return { visible: true, enabled: true, reason: null };
}

/**
 * Activation guard (UX spec 6.2.5): a control whose meaning changed in the last 500 ms
 * ignores activation, and a second activation within 500 ms is ignored.
 */
export class ActivationGuard {
  private readonly meanings = new Map<string, { meaning: string; changedAt: number }>();
  private readonly lastActivation = new Map<string, number>();

  constructor(private readonly windowMs = 500) {}

  observe(control: string, meaning: string, now: number): void {
    const prev = this.meanings.get(control);
    if (!prev || prev.meaning !== meaning) this.meanings.set(control, { meaning, changedAt: now });
  }

  allow(control: string, now: number): boolean {
    const m = this.meanings.get(control);
    if (m && now - m.changedAt < this.windowMs) return false;
    const last = this.lastActivation.get(control);
    if (last !== undefined && now - last < this.windowMs) return false;
    this.lastActivation.set(control, now);
    return true;
  }
}

export interface OwnPhoneStatus {
  tone: 'warn' | 'neutral';
  text: string;
  unreachable: boolean;
}

export interface OwnPhoneStatusOptions {
  viewAs?: boolean;
  expectedAccount?: string;
  deviceStatus?: 'none' | 'checking' | 'creating' | 'present' | 'error';
}

/**
 * Single truthful status for the current user's own phone in the workspace header.
 *
 * - Returns `null` when the user is not `available`, or when the user's phone is locally `ready`
 *   (so the header and Phone dock always agree when the phone is Ready, even if `/agents.device_online`
 *   has not yet been refreshed by the PBX registration poll).
 * - In `view_as` mode, no local engine runs for the viewed user, so backend `agent.device_online`
 *   remains authoritative.
 * - In an observer window (`in_another_tab` / `ownership.status === 'observer'`), when a verified
 *   same-account owner exists, reports whether the phone is active, connecting, or disconnected in
 *   that other window/Sidetone app instead of falsely claiming no phone is connected.
 * - For genuine local disconnections or setup blockers, returns specific actionable copy.
 */
export function ownPhoneStatus(
  agent: Agent | null,
  readiness: Readiness,
  ownership?: OwnershipSnapshot | null,
  options?: OwnPhoneStatusOptions,
): OwnPhoneStatus | null {
  if (!agent || agent.availability !== 'available') return null;

  if (options?.viewAs) {
    if (agent.device_online) return null;
    return { tone: 'warn', text: 'Not reachable: no phone connected', unreachable: true };
  }

  if (readiness.state === 'ready') return null;

  const rawOwner = ownership?.owner ?? null;
  const crossAccountOwner = Boolean(
    rawOwner && options?.expectedAccount && rawOwner.account !== options.expectedAccount,
  );
  const verifiedOwner = rawOwner && !crossAccountOwner ? rawOwner : null;

  if (readiness.state === 'in_another_tab' || ownership?.status === 'observer') {
    if (verifiedOwner) {
      const target = verifiedOwner.kind === 'app' || readiness.label === 'In Sidetone app'
        ? 'Sidetone app'
        : 'another window';
      if (verifiedOwner.state === 'registered' || verifiedOwner.state === 'active') {
        return { tone: 'neutral', text: `Phone in ${target}`, unreachable: false };
      }
      if (
        verifiedOwner.state === 'starting' ||
        verifiedOwner.state === 'connecting' ||
        verifiedOwner.state === 'reconnecting'
      ) {
        return { tone: 'warn', text: `Phone connecting in ${target}…`, unreachable: true };
      }
      return { tone: 'warn', text: `Not reachable: phone in ${target} not connected`, unreachable: true };
    }
    if (crossAccountOwner || readiness.diagnostic?.code === 'phone_active_for_another_account') {
      return { tone: 'warn', text: 'Not reachable: phone in use by another account', unreachable: true };
    }
    if (!ownership && agent.device_online) {
      const target = readiness.label === 'In Sidetone app' ? 'Sidetone app' : 'another window';
      return { tone: 'neutral', text: `Phone in ${target}`, unreachable: false };
    }
    return { tone: 'warn', text: 'Not reachable: no phone connected', unreachable: true };
  }

  if (
    ownership?.status === 'checking' ||
    options?.deviceStatus === 'checking' ||
    options?.deviceStatus === 'creating'
  ) {
    return { tone: 'warn', text: 'Connecting phone…', unreachable: true };
  }

  switch (readiness.state) {
    case 'registering':
      return { tone: 'warn', text: 'Connecting phone…', unreachable: true };
    case 'reconnecting':
      return { tone: 'warn', text: 'Reconnecting phone…', unreachable: true };
    case 'offline':
      return { tone: 'warn', text: 'Not reachable: browser offline', unreachable: true };
    case 'needs_microphone':
      return {
        tone: 'warn',
        text: readiness.label === 'Microphone blocked'
          ? 'Not reachable: microphone blocked'
          : 'Not reachable: allow microphone',
        unreachable: true,
      };
    case 'stopped':
      return { tone: 'warn', text: 'Not reachable: phone turned off', unreachable: true };
    case 'no_device':
      return { tone: 'warn', text: 'Not reachable: no phone connected', unreachable: true };
    case 'revoked':
      return { tone: 'warn', text: 'Not reachable: reconnect this phone', unreachable: true };
    case 'unsupported':
      return { tone: 'warn', text: 'Not reachable: browser not supported', unreachable: true };
    case 'no_permission':
      return { tone: 'warn', text: 'Not reachable: calling not permitted', unreachable: true };
    case 'capability_off':
      return { tone: 'warn', text: 'Not reachable: calling turned off', unreachable: true };
    case 'unconfigured':
    case 'failed':
    default:
      if (crossAccountOwner || readiness.diagnostic?.code === 'phone_active_for_another_account') {
        return { tone: 'warn', text: 'Not reachable: phone in use by another account', unreachable: true };
      }
      if (!readiness.diagnostic?.code || readiness.diagnostic.code === 'engine_unconfigured') {
        return { tone: 'warn', text: 'Not reachable: no phone connected', unreachable: true };
      }
      return { tone: 'warn', text: 'Not reachable: phone not ready', unreachable: true };
  }
}

export function reachabilityWarning(
  agent: Agent | null,
  readiness: Readiness,
  ownership?: OwnershipSnapshot | null,
  options?: OwnPhoneStatusOptions,
): string | null {
  const status = ownPhoneStatus(agent, readiness, ownership, options);
  if (!status || !status.unreachable) return null;
  if (status.text.startsWith('Not reachable:')) {
    return `Available, but n${status.text.slice(1)}.`;
  }
  return `Available, but not reachable: ${status.text.charAt(0).toLowerCase()}${status.text.slice(1).replace(/…$/, '')}.`;
}

/**
 * Merges a `/agents` collection response with the current local projection without allowing
 * an older or in-flight-superseded `/agents` read to roll back a newer availability write
 * (`version`, `availability`, `reason`), while always keeping live server presence fields
 * (`device_online`, `derived_call_state`, `queue_ids`, `last_seen_at`) fresh.
 */
export function mergeAgentsProjection(
  current: readonly Agent[] | null,
  incoming: readonly Agent[],
  options?: { selfUserId?: string; preserveSelfAvailability?: boolean },
): Agent[] {
  if (!current || current.length === 0) return [...incoming];
  const byUser = new Map(current.map(a => [a.user_id, a]));
  return incoming.map(inc => {
    const existing = byUser.get(inc.user_id);
    if (!existing) return inc;
    const isSelfProtected = Boolean(
      options?.preserveSelfAvailability &&
      options?.selfUserId &&
      inc.user_id === options.selfUserId &&
      existing.version >= inc.version,
    );
    if (existing.version > inc.version || isSelfProtected) {
      return {
        ...inc,
        availability: existing.availability,
        reason: existing.reason,
        version: existing.version,
      };
    }
    return inc;
  });
}
